import {
  AccountAvailability,
  CustomTypes,
  CustomValueTag,
  PermissionSettings,
  PermissionsGroup,
  Template
} from "PFTypes";

import { CvTemplateExportFormat } from "./cv_template";
import { Snakify } from "./snakify";
import { Threshold } from "./threshold";

export type DateFormatKey = "day_month" | "month_day" | "full_month";

export enum Currency {
  GBP = "£",
  USD = "$",
  EUR = "€",
  AUD = "$",
  NZD = "$",
  CAD = "$"
}

type DateFormat = {
  key: DateFormatKey;
  displayFormat: string;
};

export type LocalesData = {
  locale: string;
  allowedLocales: string[];
  availableLocales: string[];
  availableDateFormats: DateFormat[];
  displayDateFormat: string;
  dateFormatKey: DateFormatKey;
  twelveHourClock: boolean;
  currency: Currency;
  availableCurrencies: string[];
};

export type Logo = Snakify<{
  fullUrl: string;
  thumbnailUrl: string;
}>;

export type Customization = {
  landingDescription: string;
  landingCoverUrl: string | null;
  landingCoverColor: string | null;
  contactInfo: string;
  showTermsAndConditions: boolean;
  learningPlatformLink: string | null;
  customLinks: { id: number; title: string; url: string }[];
  termsAndConditions: string;
  defaultExportFormat?: CvTemplateExportFormat;
  linkedinUrl: string;
  facebookUrl: string;
  twitterUrl: string;
  instagramUrl: string;
  copyright: string;
  footerLinks: string;
  showFooterLogo: boolean;
} & LocalesData;

type ActiveFeature = {
  id: number;
  name: string;
};

type CVParserConfig = {
  accreditations: boolean;
  brands: boolean;
  certificates: boolean;
  current_position: boolean;
  enabled: boolean;
  experience: boolean;
  internal_experience: boolean;
  languages: boolean;
  memberships: boolean;
  name: boolean;
  override_accreditations: boolean;
  override_brands: boolean;
  override_certificates: boolean;
  override_languages: boolean;
  override_memberships: boolean;
  override_name: boolean;
  override_qualifications: boolean;
  override_sectors: boolean;
  override_skills: boolean;
  override_summary: boolean;
  qualifications: boolean;
  sectors: boolean;
  skills: boolean;
  summary: boolean;
};

type AccountConfig = {
  profile: {
    developmental_skills_limit: number;
    top_skills_limit: number;
    suspended_retention_days: number;
  };
  minimum_search_term_length: {
    activities: number;
    profiles: number;
  };
  activity: {
    roll_forward: {
      placeholder_prefix: string;
      prefix_required: boolean;
    };
  };
};

export type CurrentAccount = {
  id: number;
  customization: Customization;
  profile_view_customization: Record<
    "accomplishment_section" | "edit_photo" | "validated_skill_text",
    boolean
  >;
  full_domain: string;
  name?: string;
  active_features: ActiveFeature[];
  cv_parser_config: CVParserConfig;
  logo: Logo;
  session_ttl_in_minutes?: number;
  live_ttl_in_minutes: number;
  slo_target_url?: number;
  segment: string[];
  theme: string | null;
  config: AccountConfig;
  custom_value_tags: Snakify<CustomValueTag>[];
  clientCodename?: string | null;
};

export type MyAccountInfo = {
  availability: AccountAvailability;
  custom_types: CustomTypes;
  permissionsGroup: PermissionsGroup;
  permissionsSettings: PermissionSettings;
  templates: Template[];
  thresholds: Threshold[];
  config: {
    externalKeys: {
      pendoApiKey?: string;
    };
    bookingModule?: {
      defaultUtilizationMetric?: "billable_utilization" | "total_utilization";
    };
  };
};
